import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, PoundSterling, Recycle, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import Seo from "@/components/Seo";

const CONDITIONS = ["Like new", "Good — light wear", "Fair — visible wear", "Faulty / damaged"];

export default function Sell() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    device_type: "phone", brand: "", model: "", condition: "", storage: "",
    name: user?.name || "", email: user?.email || "", phone: "", notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.condition) return toast.error("Pick the condition of your device");
    setBusy(true);
    try {
      const { data } = await api.post("/sell-requests", form);
      setDone(data);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="sell-success">
        <CheckCircle2 className="h-16 w-16 mx-auto mb-6 text-accent" />
        <h1 className="font-display font-black uppercase tracking-tight text-4xl mb-4">Offer request received</h1>
        <p className="text-neutral-600 mb-2">Your reference is</p>
        <p className="font-display font-black text-3xl tracking-widest border-2 border-black inline-block px-6 py-3 shadow-hard mb-8" data-testid="sell-reference">{done.reference}</p>
        <p className="text-neutral-600 leading-relaxed max-w-md mx-auto">
          We'll look at your {form.brand} {form.model} and come back to you with a fair offer — usually by WhatsApp or a call to the number you gave.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16" data-testid="sell-page">
      <Seo title="Sell My Device | Appleman Tech Repair" description="Sell your used or faulty phone, tablet, laptop or games console to Appleman Tech Repair in Hyde, Tameside. Fair offers, fast payment, nothing wasted." />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
        <div className="lg:col-span-2">
          <p className="eyebrow text-accent mb-4">Sell my device</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl leading-[0.95] mb-6">Turn old tech into cash</h1>
          <p className="text-neutral-600 leading-relaxed mb-10">
            We buy used and faulty phones, tablets, laptops and games consoles. Fill in the form and we'll come back with a fair, no-nonsense offer.
          </p>
          <ul className="space-y-6">
            {[
              { icon: PoundSterling, t: "Fair offers", d: "Priced on what your device is actually worth — no lowball games." },
              { icon: ShieldCheck, t: "Faulty is fine", d: "Cracked screens and dead batteries don't scare us." },
              { icon: Recycle, t: "Nothing wasted", d: "Devices we can't resell are securely wiped and responsibly recycled." },
            ].map((f) => (
              <li key={f.t} className="flex gap-4">
                <f.icon className="h-8 w-8 shrink-0 text-accent" strokeWidth={2.5} />
                <div>
                  <h3 className="font-display font-bold uppercase tracking-wider">{f.t}</h3>
                  <p className="text-sm text-neutral-600 mt-1">{f.d}</p>
                </div>
              </li>
            ))}
          </ul>
          {!user && (
            <p className="mt-10 border-2 border-black bg-neutral-50 p-4 text-sm text-neutral-600" data-testid="sell-account-hint">
              Tip: <Link to="/login" className="font-bold underline">log in</Link> first and your offer request is saved to your account so you can track it.
            </p>
          )}
        </div>

        <form onSubmit={submit} className="lg:col-span-3 border-2 border-black shadow-hard-lg p-8 space-y-5 bg-white" data-testid="sell-form">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight">Tell us about the device</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2" htmlFor="sell-type">Device type</label>
              <select id="sell-type" value={form.device_type} onChange={(e) => set("device_type", e.target.value)} data-testid="sell-type-select"
                className="w-full border-2 border-black px-4 py-3 bg-white outline-none focus:shadow-hard-sm">
                <option value="phone">Phone</option>
                <option value="tablet">Tablet</option>
                <option value="laptop">Laptop</option>
                <option value="console">Games console</option>
                <option value="other">Something else</option>
              </select>
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="sell-storage">Storage (optional)</label>
              <input id="sell-storage" value={form.storage} onChange={(e) => set("storage", e.target.value)} placeholder="e.g. 128GB" data-testid="sell-storage-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="sell-brand">Brand</label>
              <input id="sell-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} required placeholder="e.g. Apple" data-testid="sell-brand-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="sell-model">Model</label>
              <input id="sell-model" value={form.model} onChange={(e) => set("model", e.target.value)} required placeholder="e.g. iPhone 12" data-testid="sell-model-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Condition</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CONDITIONS.map((c) => (
                <button type="button" key={c} onClick={() => set("condition", c)} data-testid={`sell-condition-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`border-2 border-black px-4 py-3 text-left text-sm font-medium shadow-hard-sm brutal-hover ${form.condition === c ? "bg-accent" : "bg-white"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight pt-4">How do we reach you?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="eyebrow block mb-2" htmlFor="sell-name">Name</label>
              <input id="sell-name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="sell-name-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="sell-email">Email</label>
              <input id="sell-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="sell-email-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="sell-phone">Phone</label>
              <input id="sell-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} required placeholder="07…" data-testid="sell-phone-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2" htmlFor="sell-notes">Notes (optional)</label>
            <textarea id="sell-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} data-testid="sell-notes-input"
              placeholder="Boxed? Any faults? Charger included?"
              className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
          </div>
          <button type="submit" disabled={busy} data-testid="sell-submit-btn"
            className="w-full border-2 border-black bg-accent py-4 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover disabled:opacity-50">
            {busy ? "Sending…" : "Get my offer"}
          </button>
        </form>
      </div>
    </div>
  );
}
